import { useEffect, useRef, useCallback, useState } from 'react';
import type { TouchEvent } from 'react';
import { useViewport } from '../../hooks/useViewport';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  /** Snap points as percentages of viewport height (e.g., [0.5, 0.9] for 50% and 90%) */
  snapPoints?: number[];
  /** Show drag handle indicator */
  showHandle?: boolean;
  className?: string;
}

/**
 * Mobile-native bottom sheet component with drag-to-dismiss gesture.
 * Slides up from the bottom with configurable snap points.
 */
export function BottomSheet({
  isOpen,
  onClose,
  title,
  children,
  snapPoints = [0.5, 0.9],
  showHandle = true,
  className = '',
}: BottomSheetProps) {
  const { prefersReducedMotion } = useViewport();
  const sheetRef = useRef<HTMLDivElement>(null);
  const [currentHeight, setCurrentHeight] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startHeight, setStartHeight] = useState(0);

  // Calculate heights based on snap points
  const getSnapHeight = useCallback((index: number) => {
    if (typeof window === 'undefined') return 0;
    return window.innerHeight * (snapPoints[index] || 0.5);
  }, [snapPoints]);

  // Initialize to first snap point when opened
  useEffect(() => {
    if (isOpen) {
      setCurrentHeight(getSnapHeight(0));
      document.body.style.overflow = 'hidden';
    } else {
      setCurrentHeight(0);
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, getSnapHeight]);

  // Handle touch start
  const handleTouchStart = (e: TouchEvent) => {
    setIsDragging(true);
    setStartY(e.touches[0].clientY);
    setStartHeight(currentHeight);
  };

  // Handle touch move
  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging) return;
    
    const deltaY = startY - e.touches[0].clientY;
    const newHeight = Math.max(0, Math.min(window.innerHeight * 0.95, startHeight + deltaY));
    setCurrentHeight(newHeight);
  };

  // Handle touch end - snap to nearest point or close
  const handleTouchEnd = () => {
    setIsDragging(false);
    
    const viewportHeight = window.innerHeight;
    const currentPercent = currentHeight / viewportHeight;
    
    // If dragged below 20% of viewport, close
    if (currentPercent < 0.2) {
      onClose();
      return;
    }

    // Find nearest snap point
    let nearestSnap = snapPoints[0];
    let minDistance = Math.abs(currentPercent - snapPoints[0]);
    
    for (const snap of snapPoints) {
      const distance = Math.abs(currentPercent - snap);
      if (distance < minDistance) {
        minDistance = distance;
        nearestSnap = snap;
      }
    }

    setCurrentHeight(viewportHeight * nearestSnap);
  };

  // Handle backdrop click
  const handleBackdropClick = () => {
    onClose();
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const transitionClasses = prefersReducedMotion || isDragging
    ? ''
    : 'transition-all duration-300 ease-out';

  if (!isOpen && currentHeight === 0) return null;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black ${transitionClasses} ${
          isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
        }`}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl ${transitionClasses} ${className}`}
        style={{
          height: currentHeight,
          maxHeight: '95vh',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag Handle */}
        {showHandle && (
          <div className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>
        )}

        {/* Header */}
        {title && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="touch-target p-2 -mr-2 text-gray-400 hover:text-gray-600 rounded-lg"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Content */}
        <div 
          className="overflow-y-auto mobile-scroll safe-area-inset-bottom"
          style={{ 
            height: title 
              ? `calc(100% - ${showHandle ? '60px' : '48px'})` 
              : `calc(100% - ${showHandle ? '20px' : '0px'})` 
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export default BottomSheet;
